import {useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import type {EagColumn} from '../../../../../../types/EagColumn';
import {HighlightedColumnLabel} from '../HighlightedColumnLabel/HighlightedColumnLabel';
import {RevealColumnButton} from '../RevealColumnButton/RevealColumnButton';

export type HiddenColumnItemProps = {
  column: EagColumn;
  onSetHidden: (key: string, hidden: boolean) => void;
  columnHidingAllowed: boolean;
  /** Highlights the row while it matches the scroll-to-mode search term. */
  isSearchMatch?: boolean;
  /** Stronger highlight for the match the cycle currently points at. */
  isCurrentMatch?: boolean;
  /** Active search term — its occurrence in the label is <mark>ed. */
  searchTerm?: string;
  /** When set, renders the per-row "scroll to column" action (shows the column first). */
  onRevealInGrid?: (key: string) => void;
};

function PlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </g>
    </svg>
  );
}

export function HiddenColumnItem(props: HiddenColumnItemProps) {
  const t = useTranslationContext();

  const matchBgClass =
    props.isCurrentMatch === true // eslint-disable-line no-nested-ternary -- Pre-existing violation
      ? 'bg-[#B3D4FF]'
      : props.isSearchMatch === true
        ? 'bg-[#DEEBFF]'
        : 'hover:bg-[#FAFBFC]';

  return (
    <div
      className={`group flex items-center gap-[6px] pr-2 h-[34px] rounded-[4px] text-[13px] text-[#42526E] ${matchBgClass}`}
      style={{paddingLeft: '30px'}}
      data-testid={`columns-panel-hidden-${props.column.key}`}
      data-search-match={props.isSearchMatch === true ? 'true' : undefined}
      data-search-current={props.isCurrentMatch === true ? 'true' : undefined}
    >
      <span
        className="flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
        title={props.column.headerName}
      >
        <HighlightedColumnLabel
          text={props.column.headerName}
          searchTerm={props.searchTerm ?? ''}
        />
      </span>
      {/* Hover/focus-revealed on hover-capable devices only (touch keeps it
          visible); the Show (+) button below stays always visible. */}
      <Show when={props.onRevealInGrid}>
        {(onRevealInGrid) => (
          <span className="flex flex-shrink-0 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:focus-within:opacity-100">
            <RevealColumnButton column={props.column} onReveal={onRevealInGrid} />
          </span>
        )}
      </Show>
      <button
        type="button"
        className="ml-auto inline-flex items-center justify-center flex-shrink-0 cursor-pointer rounded-[4px] border-0 bg-transparent hover:bg-[#F3F9FF] disabled:opacity-30 disabled:cursor-not-allowed"
        style={{color: '#0052CC', padding: '3px'}}
        aria-label={t('page.datagrid.labels.showColumnAria', {column: props.column.headerName})}
        disabled={!props.columnHidingAllowed}
        onClick={() => props.onSetHidden(props.column.key, false)}
        data-testid={`columns-panel-show-${props.column.key}`}
      >
        <PlusIcon />
      </button>
    </div>
  );
}
